import { Capacitor } from "@capacitor/core";
import type { Json } from "@nfp-tracker/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ONBOARDING_HOME_CARD_DISMISSED_KEY,
  ONBOARDING_STEPS,
  type OnboardingStep,
  type OnboardingStepId,
} from "../constants/onboardingChecklist";
import { trackEvent } from "../integrations/analytics/client";
import { supabase } from "../integrations/supabase/client";
import { useCurrentUserWhatsNew } from "./useCurrentUserWhatsNew";
import { useOwnerContext } from "./useOwnerContext";

type StepRecord = {
  completedAt?: string;
  dismissedAt?: string;
  source?: "auto" | "manual";
};

type ChecklistData = Record<string, unknown>;

type Signals = {
  notificationsEnabled: boolean;
  onWeb: boolean;
  subscriptionActive: boolean;
  hasObservation: boolean;
};

export type OnboardingStepView = {
  step: OnboardingStep;
  completed: boolean;
  dismissed: boolean;
  source: "auto" | "manual" | null;
};

export type UseOnboardingChecklistResult = {
  loading: boolean;
  steps: OnboardingStepView[];
  /**
   * The permanent "Review the latest release" step (constants/whatsNew.ts), or null for
   * accounts that get none (instructors, client views, while loading). It counts in
   * totalCount/completedCount/allComplete, and is done once the release dialog has been
   * opened or it was marked done — so each new release resurfaces Getting Started until then.
   */
  releaseStep: { releaseId: string; completed: boolean } | null;
  completedCount: number;
  totalCount: number;
  percentComplete: number;
  allComplete: boolean;
  /**
   * While the latest release is uncompleted this is the per-release dismissal, so a new release
   * brings the home card back even for users who dismissed it before; otherwise it's the
   * original, persisted dismissal.
   */
  homeCardDismissed: boolean;
  completeStep: (id: OnboardingStepId, source?: "auto" | "manual") => Promise<void>;
  dismissStep: (id: OnboardingStepId) => Promise<void>;
  dismissHomeCard: () => Promise<void>;
};

/**
 * Cached "all steps complete" flag so consumers (e.g. the side nav) can decide
 * whether to render the Get Started entry point synchronously on first paint,
 * avoiding a show-then-hide flash while the live state loads.
 */
export const ONBOARDING_COMPLETE_CACHE_KEY = "nfp.onboarding.allComplete";

export const readOnboardingCompleteCache = (): boolean | null => {
  try {
    const value = window.localStorage.getItem(ONBOARDING_COMPLETE_CACHE_KEY);
    return value === null ? null : value === "1";
  } catch {
    return null;
  }
};

// Module-level dedupe so an auto-detected completion is written + tracked once per
// session even when several hook instances are mounted (nav, home card, page).
const autoWrittenKeys = new Set<string>();

const asStepRecord = (value: unknown): StepRecord =>
  value && typeof value === "object" ? (value as StepRecord) : {};

const isStepComplete = (
  step: OnboardingStep,
  record: StepRecord,
  signals: Signals
): boolean => {
  if (record.completedAt) return true;
  if (step.autoSignal && signals[step.autoSignal]) return true;
  return false;
};

export const useOnboardingChecklist = (): UseOnboardingChecklistResult => {
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [data, setData] = useState<ChecklistData>({});
  const [signals, setSignals] = useState<Signals>({
    notificationsEnabled: false,
    onWeb: !Capacitor.isNativePlatform(),
    subscriptionActive: false,
    hasObservation: false,
  });

  // Shared, session-cached auth/owner/permission context — dedupes against whatever
  // page-level useOwnerContext() call already warmed this cache (e.g. the Observations
  // page this card renders on), instead of re-resolving the owner and re-querying
  // user_subscriptions just for this checklist's "is subscription active" signal.
  const ownerContextQuery = useOwnerContext();

  useEffect(() => {
    if (ownerContextQuery.isLoading) return;

    let cancelled = false;
    const ctx = ownerContextQuery.data;

    const load = async () => {
      if (!ctx || ctx.status !== "ready") {
        if (!cancelled) setLoading(false);
        return;
      }

      const [{ data: account }, { data: firstObservation }] = await Promise.all([
        supabase
          .from("user_accounts")
          .select("onboarding_checklist, push_notifications_enabled")
          .eq("user_id", ctx.actorUserId)
          .maybeSingle(),
        supabase
          .from("observations")
          .select("id")
          .eq("owner_user_id", ctx.couple.id)
          .limit(1)
          .maybeSingle(),
      ]);

      if (cancelled) return;

      const stored =
        account?.onboarding_checklist && typeof account.onboarding_checklist === "object"
          ? (account.onboarding_checklist as ChecklistData)
          : {};

      const subscriptionActive =
        ctx.couple.subscription_status === "active" || ctx.couple.billing_provider === "apple";

      setUserId(ctx.actorUserId);
      setData(stored);
      setSignals({
        notificationsEnabled: account?.push_notifications_enabled === true,
        onWeb: !Capacitor.isNativePlatform(),
        subscriptionActive,
        hasObservation: Boolean(firstObservation),
      });
      setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [ownerContextQuery.isLoading, ownerContextQuery.data]);

  const persist = useCallback(
    async (next: ChecklistData) => {
      setData(next);
      if (!userId) return;
      const { error } = await supabase
        .from("user_accounts")
        .update({ onboarding_checklist: next as unknown as Json })
        .eq("user_id", userId);
      if (error) {
        console.error("[OnboardingChecklist] Failed to persist progress", error);
      }
    },
    [userId]
  );

  // Persist + record auto-detected completions exactly once each.
  useEffect(() => {
    if (loading || !userId) return;

    const patch: ChecklistData = {};
    let changed = false;
    for (const step of ONBOARDING_STEPS) {
      const record = asStepRecord(data[step.id]);
      const autoComplete = step.autoSignal && signals[step.autoSignal];
      const dedupeKey = `${userId}:${step.id}`;
      if (autoComplete && !record.completedAt && !autoWrittenKeys.has(dedupeKey)) {
        autoWrittenKeys.add(dedupeKey);
        patch[step.id] = {
          ...record,
          completedAt: new Date().toISOString(),
          source: "auto",
        };
        changed = true;
        trackEvent("onboarding_step_completed", { stepId: step.id, source: "auto" });
      }
    }

    if (changed) {
      void persist({ ...data, ...patch });
    }
  }, [loading, userId, data, signals, persist]);

  const completeStep = useCallback(
    async (id: OnboardingStepId, source: "auto" | "manual" = "manual") => {
      const record = asStepRecord(data[id]);
      if (record.completedAt) return;
      trackEvent("onboarding_step_completed", { stepId: id, source });
      await persist({
        ...data,
        [id]: { ...record, completedAt: new Date().toISOString(), source },
      });
    },
    [data, persist]
  );

  const dismissStep = useCallback(
    async (id: OnboardingStepId) => {
      const record = asStepRecord(data[id]);
      if (record.dismissedAt) return;
      trackEvent("onboarding_step_dismissed", { stepId: id });
      await persist({
        ...data,
        [id]: { ...record, dismissedAt: new Date().toISOString() },
      });
    },
    [data, persist]
  );

  // A new release resurfaces Getting Started (nav entry, home card) until its step is done. It
  // is client-only state (what's-new localStorage), so it never touches the persisted checklist.
  const whatsNew = useCurrentUserWhatsNew();
  const releasePending = whatsNew.releaseAvailable && !whatsNew.reviewed;
  const { dismissHomeCard: dismissReleaseHomeCard } = whatsNew;

  const dismissHomeCard = useCallback(async () => {
    // Record both: the per-release dismissal hides the card for this release, and the original
    // one keeps it hidden afterwards (once the release step is done).
    if (releasePending) dismissReleaseHomeCard();
    if (typeof data[ONBOARDING_HOME_CARD_DISMISSED_KEY] === "string") return;
    trackEvent("getting_started_card_dismissed", {});
    await persist({
      ...data,
      [ONBOARDING_HOME_CARD_DISMISSED_KEY]: new Date().toISOString(),
    });
  }, [data, persist, releasePending, dismissReleaseHomeCard]);

  const steps = useMemo<OnboardingStepView[]>(
    () =>
      ONBOARDING_STEPS.map((step) => {
        const record = asStepRecord(data[step.id]);
        return {
          step,
          completed: isStepComplete(step, record, signals),
          dismissed: Boolean(record.dismissedAt),
          source: record.completedAt
            ? record.source ?? "manual"
            : step.autoSignal && signals[step.autoSignal]
              ? "auto"
              : null,
        };
      }),
    [data, signals]
  );

  const releaseStep = whatsNew.releaseAvailable
    ? { releaseId: whatsNew.release.id, completed: whatsNew.reviewed }
    : null;

  const totalCount = steps.length + (releaseStep ? 1 : 0);
  const completedCount =
    steps.filter((s) => s.completed).length + (releaseStep?.completed ? 1 : 0);
  const percentComplete =
    totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100);
  const allComplete =
    steps.every((s) => s.completed || s.dismissed) && (!releaseStep || releaseStep.completed);
  const homeCardDismissed = releasePending
    ? whatsNew.homeCardDismissed
    : typeof data[ONBOARDING_HOME_CARD_DISMISSED_KEY] === "string";

  // Cache the resolved completion state for synchronous first-paint decisions.
  useEffect(() => {
    if (loading) return;
    try {
      window.localStorage.setItem(
        ONBOARDING_COMPLETE_CACHE_KEY,
        allComplete ? "1" : "0"
      );
    } catch {
      /* ignore storage failures */
    }
  }, [loading, allComplete]);

  return {
    loading,
    steps,
    releaseStep,
    completedCount,
    totalCount,
    percentComplete,
    allComplete,
    homeCardDismissed,
    completeStep,
    dismissStep,
    dismissHomeCard,
  };
};
